import dayjs from "dayjs";
import {
    AlertTriangle,
    ArrowLeftRight,
    CalendarDays,
    CheckCircle2,
    Clock,
    User,
    XCircle,
} from "lucide-react";
import { DialogModal } from "../../../components/ui/dialogModal";
import { Textarea } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../../components/ui/fieldStyles";

export const SHIFT_CHANGE_STATUS_BADGE = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rejected: "bg-rose-100 text-rose-800 border-rose-200",
    cancelled: "bg-gray-100 text-gray-700 border-gray-200",
};

const ScopeOption = ({ value, selected, onSelect, title, description }) => (
    <button
        type="button"
        onClick={() => onSelect(value)}
        className={cn(
            "w-full text-left rounded-lg border p-3 transition-colors",
            selected
                ? "border-sidebar-primary bg-sidebar-primary/5 ring-1 ring-sidebar-primary/30"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
        )}
    >
        <div className="flex items-start gap-2.5">
            <span
                className={cn(
                    "mt-0.5 h-4 w-4 rounded-full border flex-shrink-0 flex items-center justify-center",
                    selected ? "border-sidebar-primary" : "border-gray-300 dark:border-gray-600",
                )}
            >
                {selected && <span className="h-2 w-2 rounded-full bg-sidebar-primary" />}
            </span>
            <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{description}</span>
            </span>
        </div>
    </button>
);

const ShiftChangeReviewDialog = ({
    open,
    onClose,
    selected,
    applicationScope,
    onScopeChange,
    adminComment,
    onCommentChange,
    loading,
    onApprove,
    onReject,
    readOnly = false,
    hideEmployeeHeader = false,
}) => (
    <DialogModal
        open={open}
        onClose={onClose}
        title={readOnly ? "Request Details" : "Review Shift Change"}
        width="600px"
        showActions={false}
    >
        {selected && (
            <div className="space-y-5">
                {!hideEmployeeHeader && (
                    <div className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sidebar-primary/10 text-sidebar-primary">
                            <User className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                    {selected.employee_name}
                                </h3>
                                <span className={cn(
                                    "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize",
                                    SHIFT_CHANGE_STATUS_BADGE[selected.status] || SHIFT_CHANGE_STATUS_BADGE.pending,
                                )}>
                                    {selected.status}
                                </span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                    <CalendarDays className="h-3.5 w-3.5" />
                                    {selected.date ? dayjs(selected.date).format("DD MMM YYYY") : "-"}
                                </span>
                                {selected.requested_on && (
                                    <span className="inline-flex items-center gap-1">
                                        <Clock className="h-3.5 w-3.5" />
                                        Submitted {selected.requested_on}
                                    </span>
                                )}
                                {selected.reviewed_on && (
                                    <span className="inline-flex items-center gap-1">
                                        Reviewed {selected.reviewed_on}
                                        {selected.approved_by_name ? ` by ${selected.approved_by_name}` : ""}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {hideEmployeeHeader && (
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={cn(
                            "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize",
                            SHIFT_CHANGE_STATUS_BADGE[selected.status] || SHIFT_CHANGE_STATUS_BADGE.pending,
                        )}>
                            {selected.status}
                        </span>
                        <span className="text-muted-foreground">
                            {selected.date ? dayjs(selected.date).format("DD MMM YYYY") : "-"}
                            {selected.requested_on ? ` · Submitted ${selected.requested_on}` : ""}
                        </span>
                    </div>
                )}

                <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-3">
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Current shift</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {selected.current_shift_name || "—"}
                        </p>
                    </div>
                    <div className="flex items-center justify-center text-muted-foreground">
                        <ArrowLeftRight className="h-4 w-4" />
                    </div>
                    <div className="rounded-lg border border-sidebar-primary/30 bg-sidebar-primary/5 p-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-sidebar-primary">Requested shift</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {selected.requested_shift_name || "—"}
                        </p>
                    </div>
                </div>

                {(selected.preview || selected.late_minutes_at_request != null) && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
                            <p className="text-xs text-muted-foreground">Late before</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {selected.preview?.late_minutes_before ?? selected.late_minutes_at_request ?? 0} min
                            </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
                            <p className="text-xs text-muted-foreground">Late after (preview)</p>
                            <p className={cn(
                                "text-lg font-semibold",
                                (selected.preview?.late_minutes_after ?? 0) > (selected.preview?.late_minutes_before ?? selected.late_minutes_at_request ?? 0)
                                    ? "text-amber-600"
                                    : "text-emerald-600",
                            )}>
                                {selected.preview?.late_minutes_after ?? 0} min
                            </p>
                        </div>
                    </div>
                )}

                {selected.reason && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Your reason</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{selected.reason}</p>
                    </div>
                )}

                {selected.payroll_published && (
                    <div className="flex gap-2 rounded-lg border border-amber-200/70 bg-amber-50/80 dark:bg-amber-950/20 dark:border-amber-900/40 px-3 py-2.5 text-xs text-amber-900 dark:text-amber-200">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <p>
                            Payroll is already published for this period. Any approved change will queue a salary correction in the next payroll cycle.
                        </p>
                    </div>
                )}

                {readOnly && selected.application_scope_label && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-sm">
                        <span className="text-xs text-muted-foreground">Applied as: </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selected.application_scope_label}</span>
                    </div>
                )}

                <div>
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {readOnly ? "Review note" : "Admin comment"}
                    </label>
                    {readOnly ? (
                        <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 min-h-[2.5rem]">
                            {selected.admin_comment || "No review note provided."}
                        </p>
                    ) : (
                        <Textarea
                            value={adminComment}
                            onChange={(e) => onCommentChange(e.target.value)}
                            placeholder="Optional note for the employee or audit trail"
                            rows={3}
                            maxLength={500}
                            className="mt-1.5"
                        />
                    )}
                </div>

                {!readOnly && (
                    <>
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">How should this apply?</p>
                            <div className="grid gap-2 sm:grid-cols-2">
                                <ScopeOption
                                    value="one_day"
                                    selected={applicationScope === "one_day"}
                                    onSelect={onScopeChange}
                                    title="This day only"
                                    description="Update attendance for the selected date only."
                                />
                                <ScopeOption
                                    value="permanent"
                                    selected={applicationScope === "permanent"}
                                    onSelect={onScopeChange}
                                    title="This day + default shift"
                                    description="Apply for this date and change the employee's default shift from today."
                                />
                            </div>
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1 border-t border-gray-200 dark:border-gray-700">
                            <Button variant="outline" disabled={loading} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button variant="destructive" disabled={loading} onClick={onReject}>
                                <XCircle className="h-4 w-4 mr-1.5" />
                                Reject
                            </Button>
                            <Button
                                disabled={loading}
                                onClick={onApprove}
                                className="bg-sidebar text-white hover:bg-sidebar-primary"
                            >
                                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                                Approve
                            </Button>
                        </div>
                    </>
                )}

                {readOnly && (
                    <div className="flex justify-end pt-1 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="outline" onClick={onClose}>Close</Button>
                    </div>
                )}
            </div>
        )}
    </DialogModal>
);

export default ShiftChangeReviewDialog;
